import React, { useState } from 'react';
import { LockIcon, SparklesIcon, UploadIcon, BadgeCheckIcon, UserRoundIcon, PaletteIcon, PanelRightIcon, MessageSquareIcon } from 'lucide-react';
import { WidgetTheme, WidgetCorner, WidgetPosition, WidgetSize, WidgetLauncher } from '../../../data/widgetConfig';
import { isLowContrastForWhite } from '../../../utils/theme';
import { TalabMark } from '../../Logo';
import { DesignSection, ControlLabel, VisualChoice, Segmented, RangeControl } from '../../settings/design/DesignControls';
import { PanelProps, LauncherSample, ColourRow, TextInput } from './WidgetBits';
type SectionId = 'branding' | 'assistant' | 'colours' | 'panel' | 'launcher';
const CORNER_RADIUS: Record<WidgetCorner, number> = {
  rounded: 14,
  soft: 8,
  square: 2
};
const WIDTH_LABEL: Record<WidgetSize, string> = {
  compact: 'Narrow',
  standard: 'Standard',
  expanded: 'Wide',
  full: 'Full screen'
};
export function WidgetAppearance({
  widget,
  update,
  brand,
  canRemoveBranding,
  onUpgrade,
  previewLauncher
}: PanelProps) {
  const [open, setOpen] = useState<SectionId | null>(null);
  const toggle = (id: SectionId) => setOpen(o => o === id ? null : id);
  const matchesShop = widget.primaryColor.toLowerCase() === brand.primary.toLowerCase() && widget.accentColor.toLowerCase() === brand.accent.toLowerCase();
  const side = widget.position === 'bottom-left' ? 'Left' : 'Right';

  /** A tiny page with the panel on the chosen edge. */
  const PageThumb = ({
    width,
    right = widget.position !== 'bottom-left',
    radius = CORNER_RADIUS[widget.corner]
  }: {
    width: number;
    right?: boolean;
    radius?: number;
  }) => <span className="relative block h-12 bg-slate-50" data-id="element-4961" data-mp="2oahacvenpp54">
      <span className="absolute left-2 top-2 w-8 h-1.5 rounded-full bg-slate-200" data-id="element-4962" data-mp="2oc244it5d41x" />
      <span className="absolute left-2 top-5 w-12 h-1.5 rounded-full bg-slate-200" data-id="element-4963" data-mp="rgw73s34yhqg" />
      <span className="absolute top-1 bottom-1 bg-white ring-1 ring-slate-200 shadow-sm" style={{
      width: `${width}%`,
      [right ? 'right' : 'left']: 4,
      borderRadius: width === 100 ? 2 : radius / 2
    }} data-id="element-4964" data-mp="miuplblcyqh3">
        <span className="block h-2.5" style={{
        background: widget.primaryColor,
        borderTopLeftRadius: width === 100 ? 2 : radius / 2,
        borderTopRightRadius: width === 100 ? 2 : radius / 2
      }} data-id="element-4965" data-mp="1agaepp8115k0" />
      </span>
    </span>;
  return <div className="space-y-3" data-id="element-4966" data-mp="1tdbnll4pbpt8">
      <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-4967" data-mp="1tdlhhjsvah47">
        Open any part to change it — the preview updates as you go.
      </p>

      {/* 1 — Talab branding */}
      <DesignSection icon={BadgeCheckIcon} title="Talab branding" summary={widget.showTalabBranding ? 'Powered by Talab' : 'White-label'} open={open === 'branding'} onToggle={() => toggle('branding')} data-id="element-4968" data-mp="1kc43c6z0spi5">
        <VisualChoice<'talab' | 'white'> columns={2} value={widget.showTalabBranding ? 'talab' : 'white'} onChange={v => update({
        showTalabBranding: v === 'talab'
      })} options={[{
        id: 'talab',
        label: 'Powered by Talab',
        preview: <span className="flex h-12 items-center justify-center bg-slate-50" data-id="element-4970" data-mp="5fj8tzovz5js">
                  <TalabMark size={20} tone="light" data-id="element-4971" data-mp="5dq2ymm3kpcu" />
                </span>
      }, {
        id: 'white',
        label: 'White-label',
        preview: <span className="flex h-12 items-center justify-center bg-slate-50" data-id="element-4972" data-mp="3nl5scr5annth">
                  <span className="w-5 h-5 rounded-md" style={{
            background: widget.primaryColor
          }} data-id="element-4973" data-mp="3nn0do6kh9hy3" />
                </span>
      }]} data-id="element-4969" data-mp="8yrvll053mqp" />
        {widget.showTalabBranding ? <p className="text-[12.5px] text-slate-500 leading-relaxed" data-id="element-4974" data-mp="2q5rwn3x9i5ed">
            Visitors trust the Talab mark as a verified assistant. We recommend keeping it.
          </p> : !canRemoveBranding && <div className="flex items-center gap-3 p-3 rounded-xl bg-warning/[0.07] ring-1 ring-warning/20" data-id="element-4975" data-mp="38qsfqyymgjm3">
              <LockIcon className="w-4 h-4 text-warning flex-shrink-0" data-id="element-4976" data-mp="3dpn7wrihbkiv" />
              <p className="flex-1 text-[12px] text-slate-600" data-id="element-4977" data-mp="3ih4zwq2iyarp">Preview only — saving needs the Scale plan.</p>
              <button onClick={onUpgrade} className="px-3 py-1.5 rounded-lg bg-primary-indigo text-white text-[12px] font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4978" data-mp="3mji59ddd06ny">
                Upgrade
              </button>
            </div>}
      </DesignSection>

      {/* 2 — Assistant */}
      <DesignSection icon={UserRoundIcon} title="Assistant" summary={widget.name || 'Add a name'} open={open === 'assistant'} onToggle={() => toggle('assistant')} data-id="element-4979" data-mp="22350rhbi6p5i">
        <div className="flex items-center gap-4" data-id="element-4980" data-mp="o6vcdjcc5ybz">
          <span className="w-14 h-14 rounded-2xl ring-1 ring-slate-200 bg-white flex items-center justify-center overflow-hidden flex-shrink-0" data-id="element-4981" data-mp="o8ohuvb7qcie">
            {widget.avatar ? <img src={widget.avatar} alt="" className="w-full h-full object-contain p-1.5" data-id="element-4982" data-mp="ffuzuq4dsz0u" /> : <SparklesIcon className="w-5 h-5 text-slate-300" data-id="element-4983" data-mp="aha00rt1khcx" />}
          </span>
          <div className="flex flex-col items-start gap-1.5" data-id="element-4984" data-mp="174oggoyp4eay">
            <label className="inline-flex items-center gap-2 px-3.5 py-2 bg-white ring-1 ring-slate-200 rounded-xl text-[13px] font-semibold text-dark-navy hover:ring-primary-indigo/50 cursor-pointer transition-shadow duration-150 ease-out" data-id="element-4985" data-mp="133uq7oxs6new">
              <UploadIcon className="w-4 h-4" data-id="element-4986" data-mp="t5onx682bf9i" />
              {widget.avatar ? 'Replace picture' : 'Upload picture'}
              <input type="file" accept="image/*" className="hidden" onChange={e => {
              const file = e.target.files?.[0];
              if (file) update({
                avatar: URL.createObjectURL(file)
              });
            }} data-id="element-4987" data-mp="3itfko0ugguc3" />
            </label>
            {widget.avatar && <button onClick={() => update({
            avatar: ''
          })} className="text-[12px] text-slate-400 hover:text-error transition-colors duration-150 ease-out" data-id="element-4988" data-mp="36bv4y6uv539k">
                Remove
              </button>}
          </div>
        </div>
        <TextInput label="Name" value={widget.name} onChange={name => update({
        name
      })} data-id="element-4989" data-mp="31ev7gkc2x8u2" />
        <TextInput label="Line under the name" value={widget.subtitle} placeholder="e.g. Usually replies instantly" onChange={subtitle => update({
        subtitle
      })} data-id="element-4990" data-mp="2nn7zvocosdvz" />
      </DesignSection>

      {/* 3 — Colours */}
      <DesignSection icon={PaletteIcon} title="Colours" summary={<>
            <span className="w-3 h-3 rounded-full ring-1 ring-black/10" style={{
        background: widget.primaryColor
      }} data-id="element-4992" data-mp="1z1k3f1717tif" />
            <span className="w-3 h-3 rounded-full ring-1 ring-black/10" style={{
        background: widget.accentColor
      }} data-id="element-4993" data-mp="1u2z02swhgtpr" />
            <span className="ml-0.5" data-id="element-4994" data-mp="290re1gmdta1u">{widget.theme === 'dark' ? 'Dark' : 'Light'}</span>
          </>} open={open === 'colours'} onToggle={() => toggle('colours')} data-id="element-4991" data-mp="2dqlft1s6pqv0">
        {!matchesShop && <button onClick={() => update({
        primaryColor: brand.primary,
        accentColor: brand.accent
      })} className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-primary-indigo/[0.04] ring-1 ring-primary-indigo/10 text-left hover:bg-primary-indigo/[0.07] transition-colors duration-150 ease-out" data-id="element-4995" data-mp="2hyto0lo3fd19">
            <SparklesIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-4996" data-mp="2mth3xw516z48" />
            <span className="flex-1" data-id="element-4997" data-mp="30ot80u52u1xt">
              <span className="block text-[13px] font-semibold text-dark-navy" data-id="element-4998" data-mp="2wpu51f2wof90">Match my shop</span>
              <span className="block text-[12px] text-slate-500" data-id="element-4999" data-mp="6zdta7p1plww">Use the same colours as your Talab shop</span>
            </span>
          </button>}
        <ColourRow label="Main colour" hint="Header, send button and launcher" value={widget.primaryColor} onChange={primaryColor => update({
        primaryColor
      })} data-id="element-5000" data-mp="bw4ch3w5cxmj" />
        {isLowContrastForWhite(widget.primaryColor) && <p className="text-[12px] text-slate-500" data-id="element-5001" data-mp="3kf21utew1t0l">That’s a light colour, so text on it switches to dark automatically.</p>}
        <ColourRow label="Highlight colour" hint="Suggestion chips and small touches" value={widget.accentColor} onChange={accentColor => update({
        accentColor
      })} data-id="element-5002" data-mp="3par6hb5rm03r" />
        <div data-id="element-5003" data-mp="31n98gxvwq5sc">
          <ControlLabel data-id="element-5004" data-mp="31nixoj94o8wz">Chat window</ControlLabel>
          <Segmented<WidgetTheme> label="Chat window theme" value={widget.theme} onChange={theme => update({
          theme
        })} options={[{
          id: 'light',
          label: 'Light'
        }, {
          id: 'dark',
          label: 'Dark'
        }]} data-id="element-5005" data-mp="31mrds5ow7zmj" />
        </div>
      </DesignSection>

      {/* 4 — Chat panel */}
      <DesignSection icon={PanelRightIcon} title="Chat panel" summary={`${WIDTH_LABEL[widget.size]} · ${side} side`} open={open === 'panel'} onToggle={() => toggle('panel')} data-id="element-5006" data-mp="2p3vt48k4curb">
        <div data-id="element-5007" data-mp="2f797vinvs1or">
          <ControlLabel data-id="element-5008" data-mp="2f7sg27nqbbpl">Width</ControlLabel>
          <VisualChoice<WidgetSize> columns={4} value={widget.size} onChange={size => update({
          size
        })} options={([['compact', 34], ['standard', 42], ['expanded', 52], ['full', 100]] as [WidgetSize, number][]).map(([id, w]) => ({
          id,
          label: WIDTH_LABEL[id],
          preview: <PageThumb width={w === 100 ? 96 : w} data-id="element-5010" data-mp="1rhoncvc2wwdd" />
        }))} data-id="element-5009" data-mp="2ag0ygmmjwgbl" />
          {widget.size === 'full' && <p className="text-[12px] text-slate-500 mt-2.5" data-id="element-5011" data-mp="2efk3145b6kf">
              Takes over the whole page — best when chatting is the main reason people visit.
            </p>}
        </div>
        {widget.size !== 'full' && <div data-id="element-5012" data-mp="778mhic562tc">
            <ControlLabel data-id="element-5013" data-mp="78ti7ba6zrss">Side of the page</ControlLabel>
            <VisualChoice<WidgetPosition> columns={2} value={widget.position} onChange={position => update({
          position
        })} options={[{
          id: 'bottom-left',
          label: 'Left',
          preview: <PageThumb width={40} right={false} data-id="element-5015" data-mp="3pmjbemd406a2" />
        }, {
          id: 'bottom-right',
          label: 'Right',
          preview: <PageThumb width={40} right data-id="element-5016" data-mp="3kln7i8srck4n" />
        }]} data-id="element-5014" data-mp="3fnbsljnlvjfk" />
          </div>}
        <div data-id="element-5017" data-mp="14tz886q9ufio">
          <ControlLabel data-id="element-5018" data-mp="14tfs8vk6m17a">Corners</ControlLabel>
          <VisualChoice<WidgetCorner> columns={3} value={widget.corner} onChange={corner => update({
          corner
        })} options={(['rounded', 'soft', 'square'] as WidgetCorner[]).map(id => ({
          id,
          label: {
            rounded: 'Rounded',
            soft: 'Soft',
            square: 'Square'
          }[id],
          preview: <span className="flex h-12 items-center justify-center bg-slate-50" data-id="element-5020" data-mp="28v7dbphugyhj">
                  <span className="w-10 h-8 bg-white ring-1 ring-slate-300" style={{
              borderRadius: CORNER_RADIUS[id] / 1.5
            }} data-id="element-5021" data-mp="2dsha5q7kccfe" />
                </span>
        }))} data-id="element-5019" data-mp="zwftz6tkq2qq" />
        </div>
      </DesignSection>

      {/* 5 — Launcher */}
      <DesignSection icon={MessageSquareIcon} title="Chat button" summary={widget.launcher === 'bubble' ? 'Round button' : `“${widget.launcherLabel || 'Ask AI'}” ${widget.launcher === 'pill' ? 'button' : 'tab'}`} open={open === 'launcher'} onToggle={() => toggle('launcher')} data-id="element-5022" data-mp="2rpw1j0hdmua0">
        <div data-id="element-5023" data-mp="2wnq2r5n7vh0">
          <ControlLabel hint="What visitors tap to open the chat" data-id="element-5024" data-mp="2x77m6rdshui">Style</ControlLabel>
          <VisualChoice<WidgetLauncher> columns={3} value={widget.launcher} onChange={launcher => {
          update({
            launcher
          });
          previewLauncher?.();
        }} options={(['bubble', 'pill', 'bar'] as WidgetLauncher[]).map(id => ({
          id,
          label: {
            bubble: 'Round',
            pill: 'Button',
            bar: 'Edge tab'
          }[id],
          preview: <span className="flex h-14 items-center justify-center bg-slate-50 overflow-hidden" data-id="element-5026" data-mp="3bbxlxhixw4gc">
                  <LauncherSample widget={widget} launcher={id} scale={id === 'bubble' ? 0.8 : 0.62} data-id="element-5027" data-mp="36cclf2o4fo1i" />
                </span>
        }))} data-id="element-5025" data-mp="3u5l10tl0nntq" />
        </div>
        {widget.launcher !== 'bubble' && <TextInput label="Button text" value={widget.launcherLabel} placeholder="Ask AI" onChange={launcherLabel => update({
        launcherLabel
      })} data-id="element-5028" data-mp="2sf63fab81tdc" />}
        <div className="space-y-4" data-id="element-5029" data-mp="1bxpbol2do09a">
          <div data-id="element-5030" data-mp="1bwnppljtptcs">
            <ControlLabel data-id="element-5031" data-mp="1c0bigihg2bqg">Distance from the side</ControlLabel>
            <RangeControl value={Math.round((widget.offsetX - 8) / 56 * 100)} onChange={v => update({
            offsetX: Math.round(8 + v / 100 * 56)
          })} low="Close" high="Far" label="Distance from the side" data-id="element-5032" data-mp="1gyv5ynborvkx" />
          </div>
          <div data-id="element-5033" data-mp="t8ipup6aesj9">
            <ControlLabel data-id="element-5034" data-mp="t891f60w4tfo">Distance from the bottom</ControlLabel>
            <RangeControl value={Math.round((widget.offsetY - 8) / 56 * 100)} onChange={v => update({
            offsetY: Math.round(8 + v / 100 * 56)
          })} low="Close" high="Far" label="Distance from the bottom" data-id="element-5035" data-mp="y60zyiw1jdsu" />
          </div>
        </div>
        {previewLauncher && <button onClick={previewLauncher} className="text-[12.5px] font-semibold text-primary-indigo hover:text-indigo-700 transition-colors duration-150 ease-out" data-id="element-5036" data-mp="af53h8gn3uf9">
            See it on the page →
          </button>}
      </DesignSection>
    </div>;
}